import { compact, isEqual } from 'lodash';
import { upperFirst } from 'lodash/fp';

import {
  ComparisonMethod,
  ComparisonMethodType,
  MatchRequirement,
} from 'dashboard/models/verification-check';

// for verifications scope is the verification check the match requirement is on
export interface MatchRequirementProfile {
  'object-id': string;
  scope: string;
  'attribute-name': string;
  'match-level-name': string;
  'match-requirement': MatchRequirement;
}

export interface MatchRequirementKeys {
  'match-group-index': number;
  'match-requirement-index': number;
  'object-id': string;
  scope: string;
}

/**
 * Returns the matching match profile given a match requiremennt and an array of match profiles
 */
export function matchingMatchRequirementProfile(
  matchRequirement: MatchRequirement,
  matchRequirementProfiles: MatchRequirementProfile[],
): MatchRequirementProfile {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  if (!matchRequirementProfiles) return null;

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return matchRequirementProfiles.find(
    (profile) =>
      profile['attribute-name'] === matchRequirement.description &&
      isEqual(profile['match-requirement'], matchRequirement),
  );
}

export function getThreshold(comparisonMethod: ComparisonMethod): number | null {
  let threshold = 0;
  if (comparisonMethod['match-level-minimum'] === 'full')
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    threshold = comparisonMethod['full-match-threshold'];
  else if (comparisonMethod['match-level-minimum'] === 'partial')
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    threshold = comparisonMethod['partial-match-threshold'];
  else if (comparisonMethod['match-level-minimum'] === 'none') {
    return null;
  }

  if (comparisonMethod.type === ComparisonMethodType.Tokenization)
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    threshold = comparisonMethod['token-match-count-minimum'];
  else if (comparisonMethod.type === ComparisonMethodType.Substring)
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    threshold = comparisonMethod['attribute-length-minimum'];
  else if (comparisonMethod.type === ComparisonMethodType.StringSimilarity)
    threshold = Math.round(threshold * 100);
  return threshold;
}

/**
 * Returns a short blurb of what a given comparison method does
 */
function generateComparisonMethodBlurb(comparisonMethod: ComparisonMethod): string {
  const threshold = getThreshold(comparisonMethod);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  if (threshold == null) return null;
  const s = threshold === 1 ? '' : 's';

  switch (comparisonMethod.type) {
    case ComparisonMethodType.DateDifferenceDay:
      return `${threshold} day${s} apart`;
    case ComparisonMethodType.DateDifferenceMonth:
      return `${threshold} month${s} apart`;
    case ComparisonMethodType.DateDifferenceYear:
      return `${threshold} year${s} apart`;
    case ComparisonMethodType.DateSimilarity:
      return `matching ${threshold} date component${s}`;
    case ComparisonMethodType.Nickname:
      return `corresponding nicknames`;
    case ComparisonMethodType.StringDifference:
      return `${threshold} character${s} off`;
    case ComparisonMethodType.StringSimilarity:
      return `${threshold}% similar`;
    case ComparisonMethodType.Substring:
      return `substrings with ${threshold}+ characters`;
    case ComparisonMethodType.Tokenization:
      return `matching ${threshold} token${s}`;
    default:
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return null;
  }
}

/**
 * Gets match-level-name from matching match requirement profile, or else generates a blurb based on the comparison methods
 */
export function matchRequirementMatchLevelName(
  matchRequirement: MatchRequirement,
  matchRequirementProfiles: MatchRequirementProfile[],
  maxComparisonMethodBlurbs = 3,
): string {
  const matchRequirementProfile = matchingMatchRequirementProfile(
    matchRequirement,
    matchRequirementProfiles,
  );
  if (matchRequirementProfile) return matchRequirementProfile['match-level-name'];
  const comparisonMethodBlurbs = compact(
    matchRequirement['comparison-methods']?.map((comparisonMethod) =>
      generateComparisonMethodBlurb(comparisonMethod),
    ),
  );

  // In the event that there are no comparison methods, return the match level minimum if that exists.
  // This is generalized to allow us to describe match requirements that don't have comparison methods.
  if (comparisonMethodBlurbs.length === 0)
    return matchRequirement['match-level-minimum'] != null
      ? upperFirst(matchRequirement['match-level-minimum'])
      : 'None';
  if (
    comparisonMethodBlurbs.length <= maxComparisonMethodBlurbs &&
    comparisonMethodBlurbs.length !== 1
  )
    comparisonMethodBlurbs[comparisonMethodBlurbs.length - 1] =
      'or ' + comparisonMethodBlurbs[comparisonMethodBlurbs.length - 1];
  const comparisonMethodBlurb = comparisonMethodBlurbs
    .slice(0, maxComparisonMethodBlurbs)
    .join(', ');
  if (comparisonMethodBlurbs.length > maxComparisonMethodBlurbs)
    return comparisonMethodBlurb + ', or...';
  return comparisonMethodBlurb;
}
